import { Divider, Flex, Layout, Spin } from 'antd';
import React, { useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import startCase from 'lodash/startCase';
import SOSColumns from './SOSColumns';
import AnomalyColumns from './AnomalyColumns';
import { ErrorMessageContext } from '../../../../context/ErrorMessageContext';
import {
  ANOMALY_TYPE,
  DMS_TYPE,
  HYPHEN,
  SOS_CALL_TYPES,
  SOS_STATUS_KEYS,
} from '../../../../utilities/constants';
import { API } from '../../../../services';
import PendingSOSCard from './PendingSOSCard';
import {
  formatTimeDifference,
  formatTimeDifferenceInHours,
  getFormattedTimeFromTimestamp,
  getErrorMessage,
} from '../../../../utilities/commonFunctions';
import InProgressSOSCard from './InProgressSOSCard';
import ResolvedSOSCard from './ResolvedSOSCard';
import AnomalyCard from './AnomalyCard';
import SOSAlertsCard from './SOSAlertsCard';
import AnomaliesCard from './AnomaliesCard';
import SafetyAnomalyMaps from '../MapView/SafetyAnomalyMaps';
import useApiPolling from '../../../../hooks/useApiPolling';
import TripTrackingModal from '../MapView/TripTrackingModal';

const { Sider, Content } = Layout;

const MonitorSafety = ({
  selectedTabKey,
  setSelectedTabKey,
  activeKey,
  setLastUpdatedTime,
  sosListRefreshKey = 0,
  mapView = false,
}) => {
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [isSoActionModalOpen, setIsSoActionModalOpen] = useState(false);
  const [isSoResolutionModelOpen, setIsSoResolutionModelOpen] = useState(false);
  const [clickedSosData, setClickedSosData] = useState('');
  const [sosListData, setSosListData] = useState(null);
  const [anomalyData, setAnomalyData] = useState({
    [ANOMALY_TYPE?.DRIVER_DROWSINESS]: [],
    [ANOMALY_TYPE?.MAJOR]: [],
    [ANOMALY_TYPE?.MINOR]: [],
  });
  const containerRef = useRef(null);
  const siderRef = useRef(null);
  const [siderHeight, setSiderHeight] = useState(0);
  const [selectedTripAndDevice, setSelectedTripAndDevice] = useState(null);
  const [isTripTrackingModalOpen, setIsTripTrackingModalOpen] = useState(false);
  const isFirstCallSos = useRef(true);
  const isFirstCallAnomaly = useRef(true);
  const [sosLoading, setSosLoading] = useState(true);
  const [anomalyLoading, setAnomalyLoading] = useState(true);
  const isCurrentTabVisible = mapView ? activeKey === '2' : activeKey === '1';
  const hasInitializedSafetyMonitoringDataRef = useRef(false);
  const [pendingSosRefreshKey, setPendingSosRefreshKey] = useState(0);

  useEffect(() => {
    if (
      hasInitializedSafetyMonitoringDataRef.current ||
      !isCurrentTabVisible ||
      !(activeKey === '1' || activeKey === '2')
    ) {
      return;
    }

    hasInitializedSafetyMonitoringDataRef.current = true;
    setPendingSosRefreshKey(0);
    getSOSData();
    getAnomalyData();
  }, [activeKey, isCurrentTabVisible]);

  useEffect(() => {
    if (sosListRefreshKey > 0) {
      setPendingSosRefreshKey(sosListRefreshKey);
    }
  }, [sosListRefreshKey]);

  useEffect(() => {
    if (
      !pendingSosRefreshKey ||
      !isCurrentTabVisible ||
      !hasInitializedSafetyMonitoringDataRef.current
    ) {
      return;
    }

    getSOSData();
    setPendingSosRefreshKey(0);
  }, [pendingSosRefreshKey, isCurrentTabVisible, activeKey]);

  const handleOnTripDetailClick = (
    data,
    trip_id,
    ridebuddy_deviceid,
    anomaly_id
  ) => {
    setSelectedTripAndDevice({
      ...data,
      trip_id,
      ridebuddy_deviceid,
      anomaly_id,
    });
    setIsTripTrackingModalOpen(true);
  };

  const getAnomalyTitle = type => {
    switch (type) {
      case 'rash_driving':
        return 'Rash Driving';
      case DMS_TYPE.DROWSINESS:
        return 'Driver Drowsiness';
      case DMS_TYPE.DISTRACTION:
        return 'Driver Distraction';
      case 'door_open':
        return 'Door Open';
      case 'overspeed':
        return 'Over Speeding';
      default:
        return 'Anomaly';
    }
  };

  const getSOSData = async () => {
    setSosLoading(isFirstCallSos.current && true);
    try {
      const response = await API.getSOSColumnData(false);
      if (response?.aborted) {
        return;
      }

      const { data } = response;
      let formattedPendingSosData =
        data[SOS_STATUS_KEYS?.PENDING]?.map(element => ({
          ...element,
          children: (
            <PendingSOSCard
              data={{
                ...element,
                trip_id: element?.trip_id,
                device_id: element?.device_id,
                location: { lat: element?.latitude, lng: element?.longitude },
                anomaly_id: element?.messageid,
              }}
              handleSoActionModalOpen={handleSoActionModalOpen}
              onTripDetailClick={handleOnTripDetailClick}
            />
          ),
          titleContent: {
            title: element?.reason ? startCase(element?.reason) : 'SOS',
            iconKey: 'sos',
            time: formatTimeDifference(element?.sos_created_at),
          },
          timestamp: getFormattedTimeFromTimestamp(element?.sos_created_at),
        })) || [];
      let formattedInProgressSosData =
        data[SOS_STATUS_KEYS?.IN_PROGRESS]?.map(element => ({
          ...element,
          children: (
            <InProgressSOSCard
              data={{
                ...element,
                trip_id: element?.trip_id,
                device_id: element?.device_id,
                location: { lat: element?.latitude, lng: element?.longitude },
                unresolvedFor: formatTimeDifferenceInHours(
                  element?.last_updated_timestamp
                ),
                reporter: element?.reporter,
                anomaly_id: element?.messageid,
              }}
              handleSoResolutionModalOpen={handleSoResolutionModalOpen}
              onTripDetailClick={handleOnTripDetailClick}
            />
          ),
          titleContent: {
            title: element?.reason ? startCase(element?.reason) : 'SOS',
            iconKey: 'sos',
            time: formatTimeDifference(element?.sos_created_at),
          },
          timestamp: getFormattedTimeFromTimestamp(element?.sos_created_at),
        })) || [];
      let formattedResolvedSosData =
        data[SOS_STATUS_KEYS?.RESOLVED]?.map(element => ({
          ...element,
          children: (
            <ResolvedSOSCard
              data={{
                ...element,
                trip_id: element?.trip_id,
                device_id: element?.device_id,
                location: { lat: element?.latitude, lng: element?.longitude },
                resolvedAt: getFormattedTimeFromTimestamp(
                  element?.last_updated_timestamp
                ),
                resolvedBy:
                  element?.call_type === 'False Alert'
                    ? element?.reporter || HYPHEN
                    : element?.resolved_by || HYPHEN,
                anomaly_id: element?.messageid,
              }}
              onTripDetailClick={handleOnTripDetailClick}
            />
          ),
          titleContent: {
            title: element?.reason ? startCase(element?.reason) : 'SOS',
            iconKey: 'sos',
            time: formatTimeDifference(element?.sos_created_at),
          },
          timestamp: getFormattedTimeFromTimestamp(element?.sos_created_at),
        })) || [];
      setSosListData({
        [SOS_STATUS_KEYS.PENDING]: formattedPendingSosData,
        [SOS_STATUS_KEYS.IN_PROGRESS]: formattedInProgressSosData,
        [SOS_STATUS_KEYS.RESOLVED]: formattedResolvedSosData,
      });
      setLastUpdatedTime(Date.now());
    } catch (error) {
      if (isFirstCallSos.current) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable fetch SOS details! Please try again after sometime.'
          )
        );
      }
    } finally {
      isFirstCallSos.current = false;
      setSosLoading(false);
    }
  };

  const getAnomalyData = async () => {
    setAnomalyLoading(isFirstCallAnomaly.current && true);
    try {
      const { data } = await API.getAnomalyColumnData(false);

      const transformedData = { ...data };

      transformedData.driver_drowsiness = transformedData?.major?.filter(
        item => item?.type === DMS_TYPE.DROWSINESS
      );

      transformedData.major = transformedData?.major?.filter(
        item =>
          item?.type !== DMS_TYPE.DROWSINESS &&
          item?.type !== DMS_TYPE.DISTRACTION
      );

      const formatAnomalyData = anomalyType =>
        transformedData[anomalyType.toLowerCase().replaceAll(/\s+/g, '_')]
          ?.filter(data => data?.type !== DMS_TYPE.DISTRACTION)
          ?.map(element => ({
            ...element,
            children: (
              <AnomalyCard
                data={{
                  ...element,
                  trip_id: element?.trip_id,
                  device_id: element?.device_id,
                  location: { lat: element?.latitude, lng: element?.longitude },
                  anomlay_id: element?.dms_id,
                }}
                onTripDetailClick={handleOnTripDetailClick}
              />
            ),
            titleContent: {
              title: getAnomalyTitle(element?.type),
              iconKey: element?.type || 'rash_driving',
              time: formatTimeDifference(element?.timestamp),
            },
            timestamp: getFormattedTimeFromTimestamp(element?.timestamp),
          }));

      const formattedDriverDrowsinessData = formatAnomalyData(
        ANOMALY_TYPE.DRIVER_DROWSINESS
      );
      const formattedMajorAnomalyData = formatAnomalyData(ANOMALY_TYPE.MAJOR);
      const formattedMinorAnomalyData = formatAnomalyData(ANOMALY_TYPE.MINOR);

      setAnomalyData({
        [ANOMALY_TYPE.DRIVER_DROWSINESS]: formattedDriverDrowsinessData,
        [ANOMALY_TYPE.MAJOR]: formattedMajorAnomalyData,
        [ANOMALY_TYPE.MINOR]: formattedMinorAnomalyData,
      });
      setLastUpdatedTime(Date.now());
    } catch (error) {
      if (isFirstCallAnomaly.current) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch Anomaly details! Please try again after some time.'
          )
        );
      }
    } finally {
      isFirstCallAnomaly.current = false;
      setAnomalyLoading(false);
    }
  };

  const isAnomalyPollingEnabled = isCurrentTabVisible && selectedTabKey === 1;
  const isSosPollingEnabled = isCurrentTabVisible && selectedTabKey === 0;
  const pollingSessionKey = `${activeKey}`;
  const prevSosPollingEnabledRef = useRef(isSosPollingEnabled);
  const prevAnomalyPollingEnabledRef = useRef(isAnomalyPollingEnabled);
  const prevSosPollingSessionKeyRef = useRef(pollingSessionKey);
  const prevAnomalyPollingSessionKeyRef = useRef(pollingSessionKey);

  useEffect(() => {
    if (
      isSosPollingEnabled &&
      (!prevSosPollingEnabledRef.current ||
        prevSosPollingSessionKeyRef.current !== pollingSessionKey)
    ) {
      isFirstCallSos.current = true;
    }
    prevSosPollingEnabledRef.current = isSosPollingEnabled;
    prevSosPollingSessionKeyRef.current = pollingSessionKey;
  }, [isSosPollingEnabled, pollingSessionKey]);

  useEffect(() => {
    if (
      isAnomalyPollingEnabled &&
      (!prevAnomalyPollingEnabledRef.current ||
        prevAnomalyPollingSessionKeyRef.current !== pollingSessionKey)
    ) {
      isFirstCallAnomaly.current = true;
    }
    prevAnomalyPollingEnabledRef.current = isAnomalyPollingEnabled;
    prevAnomalyPollingSessionKeyRef.current = pollingSessionKey;
  }, [isAnomalyPollingEnabled, pollingSessionKey]);

  useApiPolling(
    getAnomalyData,
    isAnomalyPollingEnabled,
    [activeKey, selectedTabKey],
    false,
    1 * 60 * 1000,
    true
  );
  useApiPolling(
    getSOSData,
    isSosPollingEnabled,
    [activeKey, selectedTabKey],
    false,
    1 * 60 * 1000,
    true
  );

  const handleSoActionModalOpen = data => {
    setClickedSosData(data);
    setIsSoActionModalOpen(true);
  };

  const handleSoResolutionModalOpen = data => {
    setClickedSosData(data);
    setIsSoResolutionModelOpen(true);
  };

  const updateSOSListOnSOAction = (data, clickedFrom) => {
    if (clickedFrom === 'so-action-modal') {
      setSosListData(prevData => {
        const openItem = prevData[SOS_STATUS_KEYS.PENDING]?.find(
          item => item.messageid === clickedSosData?.messageid
        );

        if (
          openItem &&
          (data?.closing_notification?.call_type ===
            SOS_CALL_TYPES.GENUINE_CALL ||
            data?.closing_notification?.call_type ===
              SOS_CALL_TYPES.FALSE_ALERT)
        ) {
          // Remove from pending
          const updatedPending = prevData[SOS_STATUS_KEYS.PENDING]?.filter(
            item => item?.messageid !== clickedSosData?.messageid
          );

          if (
            data?.closing_notification?.call_type === SOS_CALL_TYPES.FALSE_ALERT
          ) {
            // Only add to resolved
            const newResolvedItem = {
              ...openItem,
              ...data?.sos_data,
              ...data?.closing_notification,
              children: (
                <ResolvedSOSCard
                  data={{
                    ...openItem,
                    ...data?.sos_data,
                    ...data?.closing_notification,
                    resolvedAt: getFormattedTimeFromTimestamp(
                      data?.closing_notification?.last_updated_timestamp
                    ),
                    resolvedBy:
                      data?.closing_notification?.call_type === 'False Alert'
                        ? data?.sos_data?.reporter ||
                          openItem?.reporter ||
                          HYPHEN
                        : data?.closing_notification?.resolved_by || HYPHEN,
                    anomaly_id: openItem?.messageid,
                  }}
                  onTripDetailClick={handleOnTripDetailClick}
                />
              ),
              titleContent: {
                title: data?.closing_notification?.reason
                  ? startCase(data?.closing_notification?.reason)
                  : 'SOS',
                iconKey: 'sos',
                time: formatTimeDifference(clickedSosData?.sos_created_at),
              },
              timestamp: getFormattedTimeFromTimestamp(
                clickedSosData?.sos_created_at
              ),
            };
            return {
              ...prevData,
              [SOS_STATUS_KEYS.PENDING]: updatedPending,
              [SOS_STATUS_KEYS.RESOLVED]: [
                newResolvedItem,
                ...(prevData[SOS_STATUS_KEYS.RESOLVED] || []),
              ],
            };
          } else {
            // Only add to in progress
            const newInProgressItem = {
              ...openItem,
              ...data?.sos_data,
              ...data?.closing_notification,
              children: (
                <InProgressSOSCard
                  data={{
                    ...openItem,
                    ...data?.sos_data,
                    ...data?.closing_notification,
                    unresolvedFor: formatTimeDifferenceInHours(
                      data?.closing_notification?.last_updated_timestamp
                    ),
                    reporter: data.sos_data?.reporter || openItem.reporter,
                    anomaly_id: openItem.messageid,
                  }}
                  handleSoResolutionModalOpen={handleSoResolutionModalOpen}
                  onTripDetailClick={handleOnTripDetailClick}
                />
              ),
              titleContent: {
                title: data.closing_notification?.reason
                  ? startCase(data.closing_notification.reason)
                  : 'SOS',
                iconKey: 'sos',
                time: formatTimeDifference(clickedSosData.sos_created_at),
              },
              timestamp: getFormattedTimeFromTimestamp(
                clickedSosData.sos_created_at
              ),
            };
            return {
              ...prevData,
              [SOS_STATUS_KEYS.PENDING]: updatedPending,
              [SOS_STATUS_KEYS.IN_PROGRESS]: [
                newInProgressItem,
                ...(prevData[SOS_STATUS_KEYS.IN_PROGRESS] || []),
              ],
            };
          }
        }
        return prevData;
      });
    } else if (clickedFrom === 'so-resolution-modal') {
      setSosListData(prevData => {
        const openItem = prevData[SOS_STATUS_KEYS.IN_PROGRESS]?.find(
          item => item?.messageid === clickedSosData?.messageid
        );

        if (openItem) {
          // Remove from in progress
          const updatedInProgress = prevData[
            SOS_STATUS_KEYS.IN_PROGRESS
          ]?.filter(item => item?.messageid !== clickedSosData?.messageid);
          // Add to resolved
          const newResolvedItem = {
            ...openItem,
            ...data?.sos_data,
            ...data?.closing_notification,
            children: (
              <ResolvedSOSCard
                data={{
                  ...openItem,
                  ...data?.sos_data,
                  ...data?.closing_notification,
                  resolvedAt: getFormattedTimeFromTimestamp(
                    data?.closing_notification?.last_updated_timestamp
                  ),
                  resolvedBy:
                    data?.closing_notification?.call_type === 'False Alert'
                      ? data?.sos_data?.reporter || openItem?.reporter || HYPHEN
                      : data?.closing_notification?.resolved_by || HYPHEN,
                  anomaly_id: openItem?.messageid,
                }}
                onTripDetailClick={handleOnTripDetailClick}
              />
            ),
            titleContent: {
              title: data?.closing_notification?.reason
                ? startCase(data?.closing_notification?.reason)
                : 'Sos',
              iconKey: 'sos',
              time: formatTimeDifference(clickedSosData?.sos_created_at),
            },
            timestamp: getFormattedTimeFromTimestamp(
              clickedSosData?.sos_created_at
            ),
          };
          return {
            ...prevData,
            [SOS_STATUS_KEYS.IN_PROGRESS]: updatedInProgress,
            [SOS_STATUS_KEYS.RESOLVED]: [
              newResolvedItem,
              ...(prevData[SOS_STATUS_KEYS.RESOLVED] || []),
            ],
          };
        }
        return prevData;
      });
    }
  };

  useEffect(() => {
    if (!siderRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const height = entry.contentRect.height;
        setSiderHeight(height);
      }
    });

    resizeObserver.observe(siderRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [sosListData, anomalyData]);

  return (
    <Layout className="full-height">
      <Sider width="max-content" ref={siderRef}>
        <Flex
          vertical
          className="full-height"
          style={{ padding: '1rem' }}
          ref={containerRef}
        >
          <Spin
            spinning={sosLoading}
            size="large"
            wrapperClassName="chart-spinner"
          >
            <SOSAlertsCard
              selectedTabKey={selectedTabKey}
              setSelectedTabKey={setSelectedTabKey}
              sosListData={sosListData}
            />
          </Spin>
          <Divider />
          <Spin
            spinning={anomalyLoading}
            size="large"
            wrapperClassName="chart-spinner"
          >
            <AnomaliesCard
              selectedTabKey={selectedTabKey}
              setSelectedTabKey={setSelectedTabKey}
              anomalyData={anomalyData}
            />
          </Spin>
          <Divider />
        </Flex>
      </Sider>
      {mapView ? (
        <Content
          className="content-monitor-safety"
          style={{ height: siderHeight }}
        >
          <SafetyAnomalyMaps
            sosData={sosListData}
            anomalyData={anomalyData}
            selectedTabKey={selectedTabKey}
            activeKey={activeKey}
            isFirstCall={isFirstCallSos.current || isFirstCallAnomaly.current}
          />
        </Content>
      ) : (
        <Content
          className="content-monitor-safety"
          style={{ height: siderHeight }}
        >
          {selectedTabKey === 0 && (
            <SOSColumns
              sosData={sosListData}
              isSoActionModalOpen={isSoActionModalOpen}
              setIsSoActionModalOpen={setIsSoActionModalOpen}
              isSoResolutionModelOpen={isSoResolutionModelOpen}
              setIsSoResolutionModelOpen={setIsSoResolutionModelOpen}
              clickedSosData={clickedSosData}
              updateSOSList={updateSOSListOnSOAction}
              loading={sosLoading}
            />
          )}
          {selectedTabKey === 1 && (
            <AnomalyColumns
              anomalyData={anomalyData}
              loading={anomalyLoading}
            />
          )}
        </Content>
      )}
      <TripTrackingModal
        isModalOpen={isTripTrackingModalOpen}
        setIsModalOpen={setIsTripTrackingModalOpen}
        selectedTrip={selectedTripAndDevice}
      />
    </Layout>
  );
};

MonitorSafety.propTypes = {
  selectedTabKey: PropTypes.number,
  setSelectedTabKey: PropTypes.func.isRequired,
  activeKey: PropTypes.string,
  setLastUpdatedTime: PropTypes.func.isRequired,
  sosListRefreshKey: PropTypes.number,
  mapView: PropTypes.bool,
};

export default React.memo(MonitorSafety);
